import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Flower2, BookOpen, ArrowLeft, Award, Lock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { modulesAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const ModuleDetail = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModuleData = async () => {
      try {
        const [moduleRes, lessonsRes] = await Promise.all([
          modulesAPI.getById(moduleId),
          modulesAPI.getLessons(moduleId)
        ]);
        setModule(moduleRes.data.module);
        setLessons(lessonsRes.data.lessons);
      } catch (error) {
        console.error('Error fetching module:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger le module',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchModuleData();
  }, [moduleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!module) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/modules')} className="text-pink-300">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <Flower2 className="w-8 h-8 text-pink-300" />
          <h1 className="text-xl font-bold text-white">Module</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Module Header */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="flex items-start gap-4 mb-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl"
              style={{ backgroundColor: `${module.color}40` }}
            >
              {module.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">{module.title}</h2>
              <p className="text-pink-200 text-sm">{module.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-pink-300">
            <span>{lessons.length} leçons</span>
            <span>•</span>
            <span>{module.duration}</span>
            <span>•</span>
            <span>{module.level}</span>
          </div>

          {module.progress > 0 && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-pink-200 text-sm">Progression</span>
                <span className="text-white font-semibold">{module.progress}%</span>
              </div>
              <Progress value={module.progress} className="h-2 bg-purple-900/50" />
            </div>
          )}
        </Card>

        {/* Lessons List */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-white px-2">Leçons</h3>
          {lessons.map((lesson, index) => (
            <Card 
              key={lesson.id}
              className={`p-4 backdrop-blur-md border-white/20 transition-all ${
                lesson.locked 
                  ? 'bg-white/5 opacity-60 cursor-not-allowed' 
                  : 'bg-white/10 hover:bg-white/15 cursor-pointer'
              }`}
              onClick={() => !lesson.locked && navigate(`/lesson/${lesson.id}`)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold relative ${
                  lesson.completed 
                    ? 'bg-gradient-to-br from-green-400 to-green-600' 
                    : lesson.locked 
                    ? 'bg-gradient-to-br from-gray-400 to-gray-600'
                    : 'bg-gradient-to-br from-pink-400 to-purple-600'
                }`}>
                  {lesson.completed ? '✓' : index + 1}
                  {lesson.locked && (
                    <div className="absolute inset-0 bg-black/30 rounded-full flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-semibold">{lesson.title}</h4>
                    {lesson.completed && (
                      <span className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded-full">
                        Terminée
                      </span>
                    )}
                  </div>
                  <p className="text-pink-200 text-sm">{lesson.description}</p>
                  {lesson.locked && (
                    <p className="text-yellow-300 text-xs mt-1">
                      🔒 {lesson.lockReason}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-pink-300">
                    <BookOpen className="w-3 h-3" />
                    <span>{lesson.duration}</span>
                    <span>•</span>
                    <span>{lesson.points} points</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quiz Section */}
        <Card className="p-6 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-md border-yellow-400/30">
          <div className="flex items-start gap-3 mb-4">
            <Award className="w-8 h-8 text-yellow-400" />
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Quiz Final</h3>
              <p className="text-pink-100 text-sm">
                Testez vos connaissances et gagnez 100 points bonus !
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate(`/quiz/${moduleId}`)}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-semibold py-3 rounded-xl"
          >
            Commencer le Quiz
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default ModuleDetail;
